/**
 * Radial particle/confetti burst fired at the Badge Forge impact moment.
 * Dots fly out and shrink; confetti rectangles fly out spinning.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Animated, Platform, StyleSheet, View } from 'react-native';
import {
  BURST_COLORS,
  FORGE_BURST_COUNT,
  FORGE_TIMELINE,
} from '../../constants/celebrations.constants';

const USE_NATIVE = Platform.OS !== 'web';

const styles = StyleSheet.create({
  layer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  particle: {
    position: 'absolute',
  },
});

export const ForgeBurst: React.FC = () => {
  const particles = useMemo(
    () =>
      Array.from({ length: FORGE_BURST_COUNT }, (_, i) => {
        const angle = (i / FORGE_BURST_COUNT) * Math.PI * 2 + Math.random() * 0.5;
        const dist = 55 + Math.random() * 80;
        return {
          dx: Math.cos(angle) * dist,
          dy: Math.sin(angle) * dist,
          size: 4 + Math.random() * 7,
          round: Math.random() > 0.5,
          color: BURST_COLORS[Math.floor(Math.random() * BURST_COLORS.length)],
          delay: FORGE_TIMELINE.BURST_DELAY_MS + Math.random() * 120,
          duration: 900 * (0.7 + Math.random() * 0.6),
          spin: (Math.random() - 0.5) * 540,
        };
      }),
    []
  );
  const values = useRef(
    particles.map(() => ({
      progress: new Animated.Value(0),
      op: new Animated.Value(0),
    }))
  ).current;

  useEffect(() => {
    const anims = particles.map((p, i) =>
      Animated.sequence([
        Animated.delay(p.delay),
        Animated.timing(values[i].op, { toValue: 1, duration: 30, useNativeDriver: USE_NATIVE }),
        Animated.parallel([
          Animated.timing(values[i].progress, {
            toValue: 1,
            duration: p.duration,
            useNativeDriver: USE_NATIVE,
          }),
          Animated.sequence([
            Animated.delay(p.duration * 0.55),
            Animated.timing(values[i].op, {
              toValue: 0,
              duration: p.duration * 0.45,
              useNativeDriver: USE_NATIVE,
            }),
          ]),
        ]),
      ])
    );
    const burst = Animated.parallel(anims);
    burst.start();
    return () => burst.stop();
    // Mount-only fixed timeline
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <View style={styles.layer} pointerEvents="none">
      {particles.map((p, i) => (
        <Animated.View
          key={i}
          style={[
            styles.particle,
            {
              width: p.size,
              height: p.round ? p.size : p.size * 1.4,
              borderRadius: p.round ? 999 : 2,
              backgroundColor: p.color,
              opacity: values[i].op,
              transform: [
                { translateX: values[i].progress.interpolate({ inputRange: [0, 1], outputRange: [0, p.dx] }) },
                { translateY: values[i].progress.interpolate({ inputRange: [0, 1], outputRange: [0, p.dy] }) },
                {
                  rotate: values[i].progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', `${p.round ? 0 : p.spin}deg`],
                  }),
                },
                ...(p.round
                  ? [{ scale: values[i].progress.interpolate({ inputRange: [0, 1], outputRange: [1, 0.2] }) }]
                  : []),
              ],
            },
          ]}
        />
      ))}
    </View>
  );
};
